import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { authAPI } from "@/services/api";
import { getErrorMessage } from "@/utils/errorHelpers";
import { CheckCircle2, MailCheck, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function EmailVerificationPage({ user, onVerified }) {
  const navigate = useNavigate();
  const location = useLocation();
  const initialOtpSent = location.state?.otpSent === true;
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(initialOtpSent);
  const [success, setSuccess] = useState(false);
  const requestedRef = useRef(initialOtpSent);
  const email = useMemo(() => user?.email || "", [user?.email]);

  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    if (user.email_verified) {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate, user]);

  const requestCode = useCallback(async ({ silent = false } = {}) => {
    if (!email) return;
    setBusy(true);
    try {
      await authAPI.requestEmailVerification({ email });
      setSent(true);
      if (!silent) toast.success("Un nouveau code OTP a ete envoye.");
    } catch (error) {
      toast.error(getErrorMessage(error, "Impossible d'envoyer le code OTP"));
    } finally {
      setBusy(false);
    }
  }, [email]);

  useEffect(() => {
    if (!user || user.email_verified || requestedRef.current) return;
    requestedRef.current = true;
    requestCode({ silent: true });
  }, [requestCode, user]);

  const confirmCode = async (event) => {
    event.preventDefault();
    if (code.length !== 6) return;
    setBusy(true);
    try {
      await authAPI.confirmEmailVerification({ email, code });
      const me = await authAPI.getMe();
      localStorage.setItem("mmj_user", JSON.stringify(me.data));
      onVerified?.(me.data);
      setSuccess(true);
      toast.success("Votre e-mail a ete verifie avec succès.");
      setTimeout(() => navigate("/dashboard", { replace: true }), 650);
    } catch (error) {
      setCode("");
      toast.error(getErrorMessage(error, "Verification OTP impossible"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-dvh bg-[#FCFBF8] px-4 py-6 text-[#1A1D1C] sm:px-6">
      <div className="mx-auto flex min-h-[calc(100dvh-3rem)] max-w-xl items-center justify-center">
        <Card className="w-full overflow-hidden rounded-2xl border-[#E6E4E0] bg-white shadow-sm sm:shadow-lg" data-testid="email-verification-page">
          <div className="bg-[#2A4B46] px-6 py-7 text-white">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#D4A373]">
              <MailCheck size={25} />
            </div>
            <p className="mt-5 text-xs font-bold uppercase tracking-[0.18em] text-white/65">MMJ</p>
            <h1 className="mt-2 font-['Outfit'] text-3xl font-extrabold leading-tight">Confirmation e-mail</h1>
            <p className="mt-3 text-sm leading-6 text-white/75">
              Un code OTP a six chiffres a ete envoye a l'adresse indiquee lors de votre inscription.
            </p>
          </div>

          <CardHeader className="space-y-2 px-5 pb-2 pt-5 sm:px-7">
            <CardTitle className="flex items-center gap-2 text-xl text-[#2A4B46]">
              <ShieldCheck size={21} /> Verifiez votre compte
            </CardTitle>
            <CardDescription className="break-words text-sm leading-6 text-[#5C625F]">
              Saisissez le code recu sur <span className="font-semibold text-[#2A4B46]">{email}</span>. Il expire après 20 minutes et 6 essais maximum sont autorises.
            </CardDescription>
          </CardHeader>

          <CardContent className="px-5 pb-6 sm:px-7">
            {success ? (
              <div className="rounded-2xl border border-green-200 bg-green-50 p-5 text-center text-green-800">
                <CheckCircle2 className="mx-auto mb-3" size={42} />
                <p className="font-semibold">Votre e-mail a ete verifie avec succès.</p>
              </div>
            ) : (
              <form onSubmit={confirmCode} className="space-y-5">
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={code}
                    onChange={(value) => setCode(String(value).replace(/\D/g, "").slice(0, 6))}
                    containerClassName="justify-center gap-2"
                    data-testid="registration-email-otp"
                  >
                    <InputOTPGroup className="gap-2">
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <InputOTPSlot
                          key={index}
                          index={index}
                          className="h-12 w-11 rounded-xl border border-[#DAD7D1] bg-[#FCFBF8] text-lg font-bold text-[#2A4B46] shadow-none sm:h-14 sm:w-12"
                        />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                <Button
                  type="submit"
                  disabled={busy || code.length !== 6}
                  className="h-12 w-full rounded-xl bg-[#2A4B46] text-base font-semibold text-white hover:bg-[#1f3a36]"
                  data-testid="registration-email-verify-button"
                >
                  {busy ? "Verification..." : "Verifier mon e-mail"}
                </Button>

                <div className="flex flex-col items-center gap-2 rounded-xl border border-[#E6E4E0] bg-[#F8F5EF] p-4 text-center text-sm text-[#5C625F]">
                  <p>{sent ? "Le code a ete envoye. Verifiez aussi vos courriers indesirables." : "Envoi du code en cours..."}</p>
                  <button
                    type="button"
                    onClick={() => requestCode()}
                    disabled={busy}
                    className="inline-flex items-center gap-2 font-semibold text-[#2A4B46] hover:underline disabled:opacity-60"
                  >
                    <RefreshCw size={15} /> Renvoyer le code
                  </button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
